import React, { useEffect, useState } from 'react'
import { useForm, FieldValues } from 'react-hook-form'
import { Navigate, useNavigate } from 'react-router'
import { Alert, Button, Input, Logo, useUserService } from '@climate/components'
import type { LoginErrorBody, MfaEnrolmentConfirmation, MfaEnrolmentResponse } from '../types'
import { landAfterSignIn, useMfaHandover } from '../signIn'
import LogoImage from '../../assets/images/logo.png'

/**
 * Enrolling a device, for an account that does not have one yet.
 *
 * This is a wall rather than an offer: the second factor is required of
 * everybody, so a person arriving here cannot get past it to the apps. That is
 * also why the recovery codes are shown as a step of their own with something
 * to press, instead of a line of small print under the success message. They
 * are the only way back in after a lost phone, they are displayed exactly once,
 * and somebody who scrolls past them will not know that until it matters.
 */
export default function MfaSetup() {
  const navigate = useNavigate()
  const handover = useMfaHandover()
  const { register, handleSubmit, formState: { errors } } = useForm()
  const {
    startMfaEnrolment, confirmMfaEnrolment, isConfirmingMfaEnrolment
  } = useUserService(window.config.iam_service_url, window.config.session_cookie, 'iam')

  const [enrolment, setEnrolment] = useState<MfaEnrolmentResponse | undefined>()
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | undefined>()
  const [session, setSession] = useState<MfaEnrolmentConfirmation['session'] | undefined>()
  const [remoteErrors, setRemoteErrors] = useState<string | undefined>()

  const token = handover?.mfaToken

  useEffect(() => {
    if (!token) return

    startMfaEnrolment({ mfaToken: token })
      .then((data) => setEnrolment(data as MfaEnrolmentResponse))
      .catch(() => setRemoteErrors('Could not start setup. Go back and sign in again.'))
    // Once per handover. startMfaEnrolment is a new function identity on every
    // render, so depending on it would ask the server for a new secret
    // continuously and invalidate the QR the person is in the middle of
    // scanning.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token])

  if (!token) return <Navigate to="/app/login" replace />

  const confirmAction = async (data: FieldValues) => {
    setRemoteErrors(undefined)

    try {
      const result = await confirmMfaEnrolment({
        mfaToken: token,
        code: String(data.code).trim(),
      }) as MfaEnrolmentConfirmation

      setRecoveryCodes(result.recovery_codes)
      setSession(result.session)
    } catch (e: unknown) {
      const err = e as { data: { text: () => Promise<string> } }
      const { message, error, title }: LoginErrorBody & { title?: string } = JSON.parse(await err.data.text())
      setRemoteErrors(message?.[0] || error || title)
    }
  }

  if (recoveryCodes && session) {
    return (
      <RecoveryCodes
        codes={recoveryCodes}
        onAcknowledge={() => landAfterSignIn(session, handover.search, navigate)}
      />
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <Logo LogoImage={LogoImage} alt="Tellus Iam logo"/>

        <h2 className="mt-6 text-center text-2xl font-bold text-gray-900">Set up two-step verification</h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          Tellus requires a second step at sign-in. Open an authenticator app such as
          Google Authenticator, 1Password or Authy, and scan this from inside the app.
        </p>
        {/* A phone's camera hands the code to whatever app claims it (on an
            iPhone, Passwords), and the codes then live there, not in the app
            the person goes on to use. */}
        <p className="mt-1 text-center text-xs text-gray-500">
          Scanning with the camera app can save it somewhere else, such as an iPhone&apos;s Passwords.
        </p>

        <div className="mt-8">
          <div className="bg-white py-8 px-4 shadow-sm sm:rounded-lg sm:px-10">
            {enrolment ? (
              <>
                <div className="flex justify-center">
                  {/*
                    Rendered as an image from a data URI rather than injected as
                    markup. The SVG is ours, but an <img> cannot run script
                    whatever it contains, and a QR code has no reason to be
                    anything more than a picture.
                  */}
                  <img
                    src={`data:image/svg+xml;utf8,${encodeURIComponent(enrolment.qr_svg)}`}
                    alt="QR code for your authenticator app"
                    className="h-44 w-44"
                  />
                </div>

                <div className="mt-4 text-center">
                  <p className="text-xs text-gray-500">Can&rsquo;t scan it? Enter this key by hand:</p>
                  <code
                    data-testid="otp_secret"
                    className="mt-1 block break-all font-mono text-sm tracking-wide text-gray-700"
                  >
                    {enrolment.secret}
                  </code>
                </div>
              </>
            ) : (
              <p className="text-center text-sm text-gray-500">Preparing your setup code...</p>
            )}

            <form id="confirm_otp_form" className="mt-6 space-y-6" onSubmit={handleSubmit(confirmAction)}>
              <div>
                <label htmlFor="code" className="pb-2 block text-sm text-gray-700">
                  Authentication code
                </label>
                <Input
                  id="code"
                  name="code"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  register={register('code', { required: 'Required' })}
                  errors={errors}
                />
                <p className="mt-1 text-xs text-gray-500">
                  Enter the 6-digit code the app now shows, to confirm it is set up correctly.
                </p>
              </div>

              {remoteErrors &&
                <Alert type="danger" message={remoteErrors}/>
              }

              <div className="py-2">
                <Button
                  id="confirm_otp"
                  className="w-full justify-center"
                  type="submit"
                  size={3}
                  disabled={!enrolment || isConfirmingMfaEnrolment}
                >
                  {isConfirmingMfaEnrolment ? 'Verifying...' : 'Verify'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  )
}

/**
 * At module scope, not nested in the component above: a component declared
 * during render is a new type on every render, and React remounts what it
 * returns. Here that would rebuild the panel under the person reading it.
 */
function RecoveryCodes({ codes, onAcknowledge }: { codes: string[]; onAcknowledge: () => void }) {
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <Logo LogoImage={LogoImage} alt="Tellus Iam logo"/>

        <h2 className="mt-6 text-center text-2xl font-bold text-gray-900">Save your recovery codes</h2>

        <div className="mt-8">
          <div className="bg-white py-8 px-4 shadow-sm sm:rounded-lg sm:px-10">
            <Alert
              type="warning"
              message="These are shown once and cannot be retrieved later. Store them somewhere other than the phone you just set up."
            />

            <ul
              data-testid="recovery_codes"
              className="mt-4 grid grid-cols-2 gap-2 rounded-md bg-gray-50 p-4 font-mono text-sm text-gray-800"
            >
              {codes.map((code) => <li key={code}>{code}</li>)}
            </ul>

            <p className="mt-3 text-xs text-gray-500">
              Each code works once, and lets you sign in if you lose your authenticator.
            </p>

            <div className="mt-6">
              <Button
                id="acknowledge_recovery_codes"
                className="w-full justify-center"
                size={3}
                onClick={onAcknowledge}
              >
                I have saved these codes
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
